import { z } from 'zod';
import { narrationTrackSchema } from '../narration/model.js';
import { quizSchema } from '../quiz/model.js';
import { ttsCourseSettingsSchema } from '../tts/model.js';
import { interactionSchema } from '../interaction/model.js';

/**
 * `CourseData` is everything the author adds on top of the deck. It lives inside
 * the .pptx under one versioned key (contract 1 of
 * docs/plans/41-cross-cutting-contracts.md) and is the only thing that has to be
 * migrated forward; the deck IR is re-derived from the file on every open.
 */

/** Bumped whenever the stored shape changes. See ../migrations. */
export const SCHEMA_VERSION = 3;

/** The single settings key the whole add-in is allowed to touch. */
export const COURSE_DATA_KEY = 'slideforge.course.v1';

export const NAMESPACES = ['quiz', 'interaction', 'interactiveImage', 'narration'] as const;
export type Namespace = (typeof NAMESPACES)[number];

export const completionSchema = z.object({
  kind: z.enum(['allSlidesViewed', 'quizPassed', 'slidesAndQuiz', 'manual']).default('allSlidesViewed'),
  /** Fraction of slides that must be viewed, 0-1, for the slide-based criteria. */
  slideThreshold: z.number().min(0).max(1).default(1),
});

export const skinSchema = z.object({
  accentColor: z.string().default('#2F5D8C'),
  backgroundColor: z.string().default('#FFFFFF'),
  textColor: z.string().default('#1A1A1A'),
  logoMediaId: z.string().optional(),
  fontFamily: z.string().default('Segoe UI, system-ui, sans-serif'),
});

export const playerConfigSchema = z.object({
  skin: skinSchema.default({}),
  showOutline: z.boolean().default(true),
  showNotes: z.boolean().default(false),
  showProgress: z.boolean().default(true),
  allowFreeNavigation: z.boolean().default(true),
  /** Resume where the learner left off, using LMS suspend data. */
  resume: z.boolean().default(true),
});

export const slideNamespacesSchema = z.object({
  quiz: quizSchema.optional(),
  narration: narrationTrackSchema.optional(),
  interaction: interactionSchema.optional(),
  /** A v2 client permitted unknown data in the reserved M8 slot. Preserve it. */
  legacyInteraction: z.unknown().optional(),
  /** Later modules own this namespace; it round-trips untouched for now. */
  interactiveImage: z.unknown().optional(),
});

/**
 * Enough of each slide to render the authoring UI without re-reading the deck.
 *
 * Reading a .pptx takes 10-40 seconds on Office on the web (SPIKE 1), so the
 * pane does not do it on open. The consequence used to be that reopening the
 * pane showed an empty slide list and every authored quiz became invisible —
 * the data was safe in the file, but the UI could not name a single slide to
 * hang it off, and authors reported it as "my quiz disappeared". Remembering
 * the index costs a few dozen bytes per slide and makes the pane's stored work
 * visible the instant it opens.
 */
export const slideRefSchema = z.object({
  id: z.string().min(1),
  index: z.number().int().min(0),
  name: z.string().default(''),
});

export const courseDataSchema = z.object({
  schemaVersion: z.literal(SCHEMA_VERSION),
  courseId: z.string().min(1),
  /**
   * Additive with a default, so a blob written before this field existed still
   * parses and no migration is required. It is a cache of the deck, never the
   * source of truth: a fresh read always replaces it.
   */
  slideIndex: z.array(slideRefSchema).default([]),
  title: z.string().default(''),
  description: z.string().default(''),
  /** BCP-47. id-ID is the product default. */
  language: z.string().default('id-ID'),
  completion: completionSchema.default({}),
  player: playerConfigSchema.default({}),
  /** Course-wide M6 defaults and the portable pronunciation lexicon. */
  tts: ttsCourseSettingsSchema.default({}),
  /** Keyed by slide id, then by owning module namespace. */
  slides: z.record(z.string(), slideNamespacesSchema).default({}),
  updatedAt: z.string().default(''),
});

export type CourseData = z.infer<typeof courseDataSchema>;
export type SlideNamespaces = z.infer<typeof slideNamespacesSchema>;
export type PlayerConfig = z.infer<typeof playerConfigSchema>;
export type Skin = z.infer<typeof skinSchema>;
export type CompletionCriteria = z.infer<typeof completionSchema>;
export type SlideRef = z.infer<typeof slideRefSchema>;

export function emptyCourseData(courseId: string, title = ''): CourseData {
  return courseDataSchema.parse({
    schemaVersion: SCHEMA_VERSION,
    courseId,
    title,
    updatedAt: new Date(0).toISOString(),
  });
}

/** Every quiz in the deck, in slide order when a slide order is supplied. */
export function quizzesOf(
  data: CourseData,
  slideOrder?: readonly string[],
): { slideId: string; quiz: NonNullable<SlideNamespaces['quiz']> }[] {
  const entries = Object.entries(data.slides)
    .filter(([, ns]) => ns.quiz !== undefined)
    .map(([slideId, ns]) => ({ slideId, quiz: ns.quiz as NonNullable<SlideNamespaces['quiz']> }));
  if (!slideOrder) return entries;
  const rank = new Map(slideOrder.map((id, i) => [id, i]));
  return entries.sort((a, b) => (rank.get(a.slideId) ?? 0) - (rank.get(b.slideId) ?? 0));
}
